/**
 * Environment-driven feature flags.
 *
 * These are compile-time flags read from Vite env vars (VITE_*), distinct from
 * the runtime localStorage overrides in `@/utils/featureFlags`. Use these for
 * ship-dark launches: merge the code with the flag OFF, then flip the env var
 * (Vercel env → redeploy) to activate — no code change required.
 *
 * @module config/featureFlags
 */

/**
 * Metered free tier: trial-expired / non-paying users get N real questions/day
 * (backend-enforced) instead of losing access. When OFF, the app behaves exactly
 * as it does today (premium quiz types redirect to /pricing, no allowance chip,
 * no in-flow cap paywall).
 *
 * Note: `import.meta.env` values are statically inlined by Vite at BUILD time —
 * flipping the env var requires a rebuild/redeploy. The function form (vs a
 * module-load constant) exists purely so tests can stub the flag per-case
 * (vi.stubEnv / vi.mock). Default OFF — only `'true'` enables it.
 */
export const isFreeTierEnabled = (): boolean =>
  import.meta.env.VITE_FREE_TIER_ENABLED === 'true';

/**
 * Licensing path step (#411): the "Where are you getting licensed?" onboarding
 * step and the "Your path in <State>" dashboard card.
 *
 * OFF in prod until the staging persona walk is green (docs/product/
 * licensing-path-v1-plan.md, phase 5). With the flag off, onboarding is exactly
 * the flow it is today and the card never renders, so a learner who already
 * answered the questions on staging sees no half-feature in prod.
 *
 * Same build-time semantics as the free-tier flag above: function form so tests
 * can stub it per case; only the literal `'true'` enables it.
 */
export const isLicensingPathStepEnabled = (): boolean =>
  import.meta.env.VITE_LICENSING_PATH_STEP_ENABLED === 'true';

/**
 * Trial-week Option C. When "true", trial users get the trial dashboard on
 * /dashboard (Practice tab hidden). QuizSummary can show session-end actions,
 * and the full-exam picker can show how many items the learner has seen.
 * Blank/false keeps today's Dashboard, Practice, QuizSummary, and ModernTestSelector.
 * Same build-time semantics as the licensing flag: function form so tests
 * can stub it per case; only the literal 'true' enables it.
 */
export const isTrialWeekOptionCEnabled = (): boolean =>
  import.meta.env.VITE_TRIAL_WEEK_OPTION_C_ENABLED === 'true';

/**
 * In-app /checkout with Stripe Embedded Checkout. When "true", every Buy on
 * /pricing (signed in or not) goes to /checkout instead of a hosted redirect
 * or Payment Link. Only the literal 'true' enables it.
 */
export const isEmbeddedCheckoutEnabled = (): boolean =>
  import.meta.env.VITE_EMBEDDED_CHECKOUT_ENABLED === 'true';

/**
 * Six-turn public diagnostic (#580). When OFF, the existing homepage
 * diagnostic is untouched. Function form so tests can stub per case;
 * only the literal `'true'` enables it.
 */
export const isPublicDiagnosticSixTurnsEnabled = (): boolean =>
  import.meta.env.VITE_PUBLIC_DIAGNOSTIC_SIX_TURNS_ENABLED === 'true';

/**
 * First question after the aim screen. When "true", the six-turn finisher who
 * arrives signed in from the emailed link (trial already running) goes from
 * the aim screen into today's trial module (option C on) or a 5-question
 * /start session (option C off) instead of /dashboard.
 * Only the literal `'true'` enables it.
 */
export const isTryFirstQuestionEnabled = (): boolean =>
  import.meta.env.VITE_TRY_FIRST_QUESTION_ENABLED === 'true';

/**
 * The standalone /exam-date screen the exam_date_ask letter links to
 * (aswb-backend flag EXAM_DATE_ASK_ENABLED, flipped first). OFF sends the route
 * to /dashboard. Only the literal `'true'` enables it.
 */
export const isExamDateAskEnabled = (): boolean =>
  import.meta.env.VITE_EXAM_DATE_ASK_ENABLED === 'true';

/**
 * Pass Plan trial at mint (#765). The backend already starts the trial when it
 * mints the account; when "true", a minted learner with an active trial gets a
 * dashboard CTA on the report and an /onboarding walk that ends on /dashboard
 * instead of the trial grant. Only the literal `'true'` enables it.
 */
export const isPassPlanTrialAtMintEnabled = (): boolean =>
  import.meta.env.VITE_PASS_PLAN_TRIAL_AT_MINT === 'true';
